import type { AxiosRequestConfig } from 'axios'
import { apiDeleteResult, apiGet, apiPatchResult, apiPostResult } from '@/services/api/http'
import { ROUTES } from '@/constants/routes'

export type NotificationEntityType = 'sales_lead' | 'task' | 'user' | 'chat' | 'quotation' | 'estimate' | 'site_enquiry'

export interface NotificationItem {
  id: string
  type: string
  title: string
  body: string
  entityType: NotificationEntityType | null
  entityId: string | null
  payload: unknown
  readAt: string | null
  createdAt: string
}

export interface NotificationListParams {
  unread?: boolean
  page?: number
  pageSize?: number
}

export interface NotificationListResult {
  items: NotificationItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  unreadCount: number
}

/** Same tag the server gives the phone banner (fcmSendService / pushService):
 * one banner per record, else per notification. */
export function notificationTag(notification: Pick<NotificationItem, 'id' | 'entityType' | 'entityId'>): string {
  return notification.entityType && notification.entityId
    ? `${notification.entityType}:${notification.entityId}`
    : notification.id
}

/** Ephemeral `notification.sync` frame — another device/tab read, deleted or cleared. */
export interface NotificationSyncEvent {
  action: 'read' | 'unread' | 'read_all' | 'read_entity' | 'deleted' | 'cleared'
  ids?: string[]
  entityType?: NotificationEntityType
  entityId?: string
  readAt?: string
  unreadCount: number
}

/** Deep-link target for a notification's underlying record, if any. */
export function resolveNotificationEntityHref(notification: NotificationItem): string | null {
  if (notification.type === 'task.overdue_digest') {
    return `${ROUTES.TASKS}?reminder=overdue`
  }
  if (notification.type.startsWith('system.backup_')) {
    return ROUTES.SETTINGS_BACKUPS
  }
  // Leads Inbox: the morning "N waiting" opens the tab; a new enquiry opens itself.
  if (notification.type === 'site_enquiry.waiting') {
    return `${ROUTES.SALES_LEADS}?tab=inbox`
  }
  if (notification.entityType === 'site_enquiry' && notification.entityId) {
    return ROUTES.SALES_LEADS_INBOX_VIEW.replace(':id', notification.entityId)
  }
  if (notification.entityType === 'sales_lead' && notification.entityId) {
    const leadHref = ROUTES.SALES_LEADS_VIEW.replace(':id', notification.entityId)
    // Callback reminders open the query with its Call button highlighted.
    return notification.type.startsWith('sales_lead.callback_') ? `${leadHref}?call=next` : leadHref
  }
  if (notification.entityType === 'task' && notification.entityId) {
    return ROUTES.TASKS_VIEW.replace(':id', notification.entityId)
  }
  if (notification.entityType === 'user' && notification.entityId) {
    return ROUTES.USERS_VIEW.replace(':id', notification.entityId)
  }
  if (notification.entityType === 'chat' && notification.entityId) {
    return `/messenger/${notification.entityId}`
  }
  if (notification.entityType === 'quotation' && notification.entityId) {
    return ROUTES.QUOTATION_VIEW.replace(':id', notification.entityId)
  }
  if (notification.entityType === 'estimate' && notification.entityId) {
    return ROUTES.ESTIMATE_VIEW.replace(':id', notification.entityId)
  }
  return null
}

export const notificationService = {
  list: (
    params: NotificationListParams = {},
    config?: AxiosRequestConfig,
  ): Promise<NotificationListResult> =>
    apiGet<NotificationListResult>('/notifications', { ...config, params }),

  markRead: async (id: string): Promise<NotificationItem> => {
    const { data } = await apiPatchResult<NotificationItem>(`/notifications/${id}/read`)
    return data
  },

  markUnread: async (id: string): Promise<NotificationItem & { unreadCount: number }> => {
    const { data } = await apiPatchResult<NotificationItem & { unreadCount: number }>(`/notifications/${id}/unread`)
    return data
  },

  markAllRead: async (): Promise<{ updated: number }> => {
    const { data } = await apiPostResult<{ updated: number }>('/notifications/read-all')
    return data
  },

  /** Marks every unread notification for one record as read at once — used
   * when opening a task/lead view, regardless of how the visitor got there. */
  markReadForEntity: async (
    entityType: NotificationEntityType,
    entityId: string,
  ): Promise<{ updated: number; unreadCount: number }> => {
    const { data } = await apiPostResult<{ updated: number; unreadCount: number }>(
      '/notifications/read-entity',
      { entityType, entityId },
    )
    return data
  },

  delete: async (id: string): Promise<{ unreadCount: number }> => {
    const { data } = await apiDeleteResult<{ unreadCount: number }>(`/notifications/${id}`)
    return data
  },

  /** Clear all — every notification of the signed-in user. */
  deleteAll: async (): Promise<{ deleted: number; unreadCount: number }> => {
    const { data } = await apiDeleteResult<{ deleted: number; unreadCount: number }>('/notifications')
    return data
  },
}
